import {
  WEBSITE_API_BASE_ENDPOINT,
  REGEX_ALLOWED_COOKIE_PREFIX,
  WHITELIST_QUERYSTRING_VARS,
  SESSION_STORAGE_KEYS
} from './constants';
import Introspect from './introspect';
import type {
  RequiredPageViewParams,
  UtmParams,
  InternalAttributionParams,
  AdNetworkParams,
  ScreenDisplayParams,
  QueryStringObject,
  WhitelistCookieObject,
  CalendlyEventData,
  UserInputValidationArgs
} from './marops-types';
import UserInputValidation from './validation';
import { mopsFormEvent } from './forms/custom-events';

const sendPostRequest = async (url: string, data: object|null) => {
  const response: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data)
  });

  const responseData = await response.json();

  if (!response.ok) {
    return new Error(responseData.error);
  } else {
    return responseData;
  }
};

const marOps = {
  async pageView(): Promise<string|undefined> {
    const payload: object = Object.assign(
      marOps.requiredPageView(),
      marOps._setReferrers(),
      marOps._parseCookies(),
      marOps.queryStringToObject(),
      marOps.attributionParams(),
      marOps.utmParams(),
      marOps.adNetworkParams(),
      marOps.screenParams(),
      marOps._gpcPref(),
      marOps._dntPref(),
      marOps._partnerStackReferral(),
    );
    try {
      const resp = await sendPostRequest(`${WEBSITE_API_BASE_ENDPOINT()}/visit`, payload);

      if (Object.prototype.hasOwnProperty.call(resp, 'mops_xid')) {
        const xid = resp.mops_xid;
        window.localStorage.setItem('mops_xid', xid);

        window.dispatchEvent(new CustomEvent('mops.page.view', {
            detail: mopsFormEvent('mops.page.view', { formId: '', reference_number: '' })
        }));
        return xid;
      }
    } catch(err: unknown) {
      const error = err as Error;
      return error.message
    }
  },
  requiredPageView(): RequiredPageViewParams {
    const urlParams = new URLSearchParams(window.location.search)
    const urlXid = urlParams.get('mops_xid');
    if (urlXid) {
      window.localStorage.setItem('mops_xid', urlXid);
    }
    return {
      mops_xid: urlXid || window.localStorage.getItem('mops_xid') || '',
      page_url: window.location.href,
    };
  },
  attributionParams(): InternalAttributionParams {
    const pageUrl = `https://${window.location.host}${window.location.pathname}`;  // Remove potential PII data from URL
    return {
      affinity: window.sessionStorage.getItem('affinity') || '',
      partner: window.sessionStorage.getItem('partner') || Introspect.brandDefaults(window.location.host).partner,
      partner_plan: window.sessionStorage.getItem('partner_plan') || Introspect.brandDefaults(window.location.host).partner_plan,
      sales_rep: window.sessionStorage.getItem('sales_rep') || '',
      promo: window.sessionStorage.getItem('promo') || '',
      initial_landing_page: window.localStorage.getItem('utm_initial_landing_page') || window.location.href,
      initial_referrer: window.localStorage.getItem('utm_initial_referrer') || document.referrer || pageUrl,
      last_referrer: window.localStorage.getItem('utm_last_referrer') || document.referrer || pageUrl,
      utm_visits: window.localStorage.getItem('utm_visits') || 1,
      dg: window.sessionStorage.getItem('dg') || '',
    };
  },
  utmParams(): UtmParams {
    return {
      utm_source: window.localStorage.getItem('utm_source') || '',
      utm_medium: window.localStorage.getItem('utm_medium') || '',
      utm_campaign: window.localStorage.getItem('utm_campaign') || '',
      utm_content: window.localStorage.getItem('utm_content') || '',
      utm_term: window.localStorage.getItem('utm_term') || '',
    };
  },
  adNetworkParams(): AdNetworkParams {
    return {
      google_click_id: window.sessionStorage.getItem('google_click_id') || '',
      linkedin_click_id: window.sessionStorage.getItem('linkedin_click_id') || '',
      facebook_click_id: window.sessionStorage.getItem('facebook_click_id') || '',
      microsoft_click_id: window.sessionStorage.getItem('microsoft_click_id') || '',
    };
  },
  screenParams(): ScreenDisplayParams {
    const orientation  = (window.screen ? window.screen.width >= window.screen.height ? 'landscape' : 'portrait' : '');
    const screenWidth  = (window.screen ? window.screen.width : 0)
    const screenHeight = (window.screen ? window.screen.height : 0);
    const colorDepth   = (window.screen ? window.screen.colorDepth : 0)
    return {
      screen_width: screenWidth,
      screen_height: screenHeight,
      screen_color_depth: colorDepth,
      screen_orientation: orientation,
    };
  },
  queryStringToObject(): QueryStringObject {
    const searchParams = new URLSearchParams(window.location.search);
    const kvPairs: QueryStringObject = {};
    for (const [key, value] of searchParams.entries()) {
      if (WHITELIST_QUERYSTRING_VARS.includes(key)) {
          let internalKey = key;
          if (key === 'rep')       internalKey = 'sales_rep'
          if (['gclid', 'gbraid', 'wbraid'].includes(key)){
            internalKey = 'google_click_id'
          }
          if (key === 'fbclid')    internalKey = 'facebook_click_id'
          if (key === 'li_fat_id') internalKey = 'linkedin_click_id'
          if (key === 'msclkid')   internalKey = 'microsoft_click_id'
          if (SESSION_STORAGE_KEYS.includes(internalKey)) {
            window.sessionStorage.setItem(internalKey, value);
          } else {
            window.localStorage.setItem(internalKey, value);
          }
          kvPairs[internalKey as keyof QueryStringObject] = value;
        }
    }
    return kvPairs;
  },
  _setReferrers(): void {
    const pageUrl = `https://${window.location.host}${window.location.pathname}`;
    const landingPage = window.localStorage.getItem('utm_initial_landing_page') || window.location.href;
    const referrerFirst = window.localStorage.getItem('utm_initial_referrer') || document.referrer || pageUrl;
    const referrerLast = document.referrer || referrerFirst;

    window.localStorage.setItem('utm_initial_landing_page', landingPage);
    window.localStorage.setItem('utm_initial_referrer', referrerFirst);
    window.localStorage.setItem('utm_last_referrer', referrerLast);
  },
  _parseCookies(): object {
    const cookies = document.cookie.toString().split('; ');
    const obj: WhitelistCookieObject = {};
    cookies.map((cookie) => {
      const [key, value] = cookie.toString().split('=');
      if (REGEX_ALLOWED_COOKIE_PREFIX.test(key)) {
        obj[key as keyof WhitelistCookieObject] = value;
      }
      return cookie;
    });
    return obj;
  },
  _gpcPref(): string {
    const gpcPref = (!navigator.globalPrivacyControl ? '0' : navigator.globalPrivacyControl.toString());
    window.localStorage.setItem('gpc_pref', gpcPref);
    return gpcPref;
  },

  _dntPref(): string {
    const dntPref = (!navigator?.doNotTrack ? '0' : navigator.doNotTrack.toString());
    window.localStorage.setItem('dnt_pref', dntPref);
    return dntPref;
  },
  async sendFormPayload(formPayload: object): Promise<object|Error> {
    const payload = Object.assign({},
      formPayload,
      marOps.requiredPageView(),
      marOps.attributionParams(),
      marOps.utmParams(),
      marOps._parseCookies(),
      marOps.adNetworkParams(),
      marOps.screenParams(),
      marOps._partnerStackReferral(),
    );
    return await sendPostRequest(`${WEBSITE_API_BASE_ENDPOINT()}/form`, payload);
  },
  trackConversion(event: string): boolean {
    if (Object.prototype.hasOwnProperty.call(window, 'dataLayer')) {
      window.dataLayer?.push({ event: event });
      return true;
    } else {
      // TODO: Instrument DataDog error tracing/logging here
      return false;
    }
  },
  trackClick(event: Event): boolean {
    try {
      const t = event.target as HTMLElement;
      const btnText: string = (t && t.innerText ? t.innerText : '');
      const eventData = {
        event: 'cta_button_click',
        button_text: btnText
      };
      if (Object.prototype.hasOwnProperty.call(window, 'dataLayer')) {
        window.dataLayer?.push(eventData);
        return true;
      } else {
        throw new Error('No TagManager.dataLayer');
      }
    } catch (err: unknown) {
      const error = err as Error;
      console.error('Error tracking CTA click event: ', error.message);
      return false;
    }
  },
  marketoCommon() {
    const payload = {
      // TODO: Uncomment this line once confirmed exists on all forms.
      // affinityVariable: window.sessionStorage.getItem('affinity') || '',
      Sales_Token__c: window.sessionStorage.getItem('sales_rep') || '',
      Referral_Token__c: window.sessionStorage.getItem('partner') || '',
      utm_campaign__c: window.localStorage.getItem('utm_campaign') || '',
      utm_source__c: window.localStorage.getItem('utm_source') || '',
      utm_medium__c: window.localStorage.getItem('utm_medium') || '',
      utm_content__c: window.localStorage.getItem('utm_content') || '',
      utm_term__c: window.localStorage.getItem('utm_term') || '',
      utm_initial_point_of_origin__c: window.localStorage.getItem('utm_initial_referrer') || '',
      utm_initial_landing_page__c: window.localStorage.getItem('utm_initial_landing_page') || '',
      utm_last_landing_page__c: window.localStorage.getItem('utm_last_referrer') || '',
      GCLID__c: window.sessionStorage.getItem('google_click_id') || '',
      FBCLID__c: window.sessionStorage.getItem('facebook_click_id') || '',
      LinkedIn_Click_ID__c: window.sessionStorage.getItem('linkedin_click_id') || '',
      Microsoft_Click_ID__c: window.sessionStorage.getItem('microsoft_click_id') || '',
      mops_xid__c: window.localStorage.getItem('mops_xid') || '',
    }

    // Check for PartnerStack JS SDK and append the token to the form payload
    if (window.growsumo) {
      const urlParams = new URLSearchParams(window.location.search);
      const psKey = window.sessionStorage.getItem('gspk') || urlParams.get('gspk') || null;
      psKey ? Object.assign(payload, { PartnerStack_Partner_Key__c: atob(psKey) }) : null;
    }
    return payload;
  },
  async sendCalendlyData(eventData: CalendlyEventData) {
    const payload = Object.assign({},
      eventData,
      marOps.requiredPageView(),
      marOps.attributionParams(),
      marOps.utmParams(),
      marOps._parseCookies(),
      marOps.adNetworkParams(),
      marOps.screenParams(),
      marOps._gpcPref(),
    )
    return await sendPostRequest(`${WEBSITE_API_BASE_ENDPOINT()}/hooks/calendly`, payload);
  },
  _partnerStackReferral() {
    const partnerStack = window.sessionStorage.getItem('gspk') || window.sessionStorage.getItem('ps_partner_key');
    const partnerKey = partnerStack ? atob(partnerStack) : '';
    return { partnerstack_token: partnerKey }
  },
  validator(config: UserInputValidationArgs) {
    return new UserInputValidation(config)
  },
  async logEvent(data: Record<string, any>) {
    try {
      const resp = await sendPostRequest(`${WEBSITE_API_BASE_ENDPOINT()}/event-reporter`, data);
      return resp;
    } catch(err: unknown) {
      const error = err as Error;
      return { error: error.message };
    }
  }
};

export default marOps;
